'use strict'

import Promise from 'promise'

class Recorder {
  constructor(audioContext) {
    this.audioContext = audioContext
    this.audioInput = null
    this.bufferSize = 4096

    this.recordedData = []
    this.recording = false
    this.recordingLength = 0
    this.stream = null
    this.sampleRate = null
  }

  init(stream) {
    // reset any previous data
    this.recordedData = []
    this.recordingLength = 0
    this.stream = stream

    if (this.audioContext.createJavaScriptNode) {
      this.audioNode = this.audioContext.createJavaScriptNode(this.bufferSize, 1, 1)
    } else if (this.audioContext.createScriptProcessor) {
      this.audioNode = this.audioContext.createScriptProcessor(this.bufferSize, 1, 1)
    } else {
      throw new Error('WebAudio not supported!')
    }

    this.audioNode.connect(this.audioContext.destination)
  }
  start() {
    this.recording = true
    this.audioInput = this.audioContext.createMediaStreamSource(this.stream)
    this.audioInput.connect(this.audioNode)
    this.audioNode.onaudioprocess = this.onAudioProcess.bind(this)
    this.sampleRate = this.audioContext.sampleRate
  }
  onAudioProcess(event) {
    if (!this.recording) {
      return
    }

    this.recordedData.push(new Float32Array(event.inputBuffer.getChannelData(0)))
    this.recordingLength += this.bufferSize
  }
  stop() {
    return new Promise((resolve, reject) => {
      this.recording = false
      this.stream.getTracks().forEach(track => {
        track.stop()
      })

      this.audioInput.disconnect()
      this.audioNode.disconnect()

      this.exportWav({
        sampleRate: this.sampleRate,
        recordingLength: this.recordingLength,
        data: this.recordedData
      }).then(({ buffer, view }) => {
        const blob = new Blob([view], { type: 'audio/wav' })
        resolve(blob)
      })
    })
  }

  exportWav(config, callback) {
    return new Promise((resolve, reject) => {
      let data = config.data.slice(0)
      const sampleRate = config.sampleRate
      data = joinBuffers(data, config.recordingLength)

      function joinBuffers(channelBuffer, count) {
        const result = new Float64Array(count)
        let offset = 0
        const lng = channelBuffer.length

        for (let index = 0; index < lng; index++) {
          var buffer = channelBuffer[index]
          result.set(buffer, offset)
          offset += buffer.length
        }

        return result
      }

      function writeUTFBytes(view, offset, string) {
        const lng = string.length
        for (let index = 0; index < lng; index++) {
          view.setUint8(offset + index, string.charCodeAt(index))
        }
      }

      const dataLength = data.length

      // create wav file
      const buffer = new ArrayBuffer(44 + dataLength * 2)
      const view = new DataView(buffer)

      writeUTFBytes(view, 0, 'RIFF') // RIFF chunk descriptor/identifier
      view.setUint32(4, 44 + dataLength * 2, true) // RIFF chunk length
      writeUTFBytes(view, 8, 'WAVE') // RIFF type
      writeUTFBytes(view, 12, 'fmt ') // format chunk identifier, FMT sub-chunk
      view.setUint32(16, 16, true) // format chunk length
      view.setUint16(20, 1, true) // sample format (raw)
      view.setUint16(22, 1, true) // mono (1 channel)
      view.setUint32(24, sampleRate, true) // sample rate
      view.setUint32(28, sampleRate * 2, true) // byte rate (sample rate * block align)
      view.setUint16(32, 2, true) // block align (channel count * bytes per sample)
      view.setUint16(34, 16, true) // bits per sample
      writeUTFBytes(view, 36, 'data') // data sub-chunk identifier
      view.setUint32(40, dataLength * 2, true) // data chunk length

      // write the PCM samples
      let index = 44
      // eslint-disable-next-line id-length
      for (let i = 0; i < dataLength; i++) {
        view.setInt16(index, data[i] * 0x7fff, true)
        index += 2
      }

      resolve({
        buffer: buffer,
        view: view
      })
    })
  }
}

export default Recorder
